
import React, { useRef } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { phoneValidation } from "validationSchemas/common";
import { useLoadScript } from "@react-google-maps/api";
import AddressAutocomplete from "./AddressAutocomplete";
import { createMembershipProfileAddress } from "api/salesOrderApi";
import { resolveMembershipIdForAddresses } from "../utils/orderPartyIdentity";

type OwnerOption = {
  value: string;
  label: string;
  membershipId?: string;
  membership_id?: string;
} | null;
type UseType = "billing" | "shipping";

const DEFAULT_TEN_ZEROS = "0000000000";
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

type AddressFormValues = {
  label_of_address: string;
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  cityCode: string;
  state: string;
  stateCode: string;
  zipcode: string;
  country: string;
  countryCode: string;
  latitude: string | number;
  longitude: string | number;
};

function buildAddressFormInitialValues(
  prefill?: AddressFormModalProps["prefill"]
): AddressFormValues {
  const base: AddressFormValues = {
    label_of_address: "",
    firstName: "",
    lastName: "",
    company: "",
    phone: DEFAULT_TEN_ZEROS,
    addressLine1: "",
    addressLine2: "",
    city: "",
    cityCode: "",
    state: "",
    stateCode: "",
    zipcode: "",
    ...US_COUNTRY_DEFAULTS,
    latitude: "",
    longitude: "",
  };
  if (!prefill) return base;

  const merged: AddressFormValues = { ...base, ...prefill };
  if (!merged.country?.trim()) {
    merged.country = US_COUNTRY_DEFAULTS.country;
    merged.countryCode = US_COUNTRY_DEFAULTS.countryCode;
  }
  return merged;
}

export interface AddressFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  owner: OwnerOption;                 // selected person — value is membership_id
  membershipId?: string | null;       // optional override; else derived from owner.value
  /** When true with officeId, POST /offices/{id}/add-address (office ± team member, no agent). */
  createOnOffice?: boolean;
  officeId?: string | null;
  apiBase?: string;                   // defaults to env
  useType?: UseType;                  // default 'billing'
  userType?: string;                   // "agent" | "office" - legacy hint; membership preferred when person selected
  prefill?: Partial<{
    label_of_address: string;
    firstName: string;
    lastName: string;
    company: string;
    phone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  }>;
  onCreated: (
    createdRaw: any,
    createdOption: { value: string; label: string; raw: any },
    useType: UseType
  ) => void;
  afterCreateRefresh?: () => Promise<void> | void; // call your fetchUserAddresses / fetchOfficeAddresses
}

const AddressFormModal: React.FC<AddressFormModalProps> = ({
  isOpen,
  onClose,
  token,
  owner,
  membershipId: membershipIdOverride,
  createOnOffice = false,
  officeId = null,
  apiBase = process.env.REACT_APP_API_URL || "",
  useType = "billing",
  userType,
  prefill,
  onCreated,
  afterCreateRefresh,
}) => {
  const schema = yup.object({
    // label_of_address: yup.string().required("Label is required"),
    // firstName: yup.string().required("First name is required"),
    // lastName: yup.string().required("Last name is required"),
    phone: phoneValidation,
    addressLine1: yup.string().required("Address line 1 is required"),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zipcode: yup.string().required("Zip Code is required"),
    country: yup.string().required("Country is required"),
  });
  const libraries: Array<"places"> = ["places"];
  const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;

  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };

  const formik = useFormik({
    initialValues: buildAddressFormInitialValues(prefill),
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting, setTouched }) => {
      // console.log("Form values:", values);
      try {
        if (!owner && !createOnOffice) {
          alert("Select a user (Agent/Team Member) first.");
          return;
        }
        const addressPayload = {
          label: values?.label_of_address,
          addressLine1: values?.addressLine1,
          addressLine2: values?.addressLine2,
          city: values?.city,
          cityCode: values?.cityCode,
          state: values?.state,
          stateCode: values?.stateCode,
          zip: values?.zipcode,
          country: values?.country,
          countryCode: values?.countryCode,
          phone: withDefault(values.phone),
          latitude: toCoordinate(values?.latitude),
          longitude: toCoordinate(values?.longitude),
        };

        const routeMembershipId = resolveMembershipIdForAddresses(
          owner,
          membershipIdOverride
        );
        const officeCreateId = String(officeId || "").trim();
        // Office (+ optional team member, no agent) → POST /offices/{id}/add-address.
        // Agent selected → membership profile addresses.
        const useOfficeCreate = createOnOffice === true && !!officeCreateId;

        let addressId = "";
        let createdRaw: Record<string, unknown>;

        if (useOfficeCreate) {
          const requestBody = {
            ...addressPayload,
            label: values?.label_of_address,
          };
          const res = await fetch(
            `${apiBase}/offices/${encodeURIComponent(officeCreateId)}/add-address`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(requestBody),
            }
          );
          if (!res.ok) {
            const errText = await res.text().catch(() => "");
            throw new Error(errText || "Failed to add office address");
          }
          const data = await res.json().catch(() => ({}));
          addressId = String(
            data?.address_id ||
              data?.id ||
              data?._id ||
              data?.address?.id ||
              data?.address?._id ||
              ""
          ).trim();
          if (!addressId) {
            // Some office endpoints return the updated office; fall back to client id for UI.
            addressId = `office-addr-${Date.now()}`;
          }
          createdRaw = {
            _id: addressId,
            label: values.label_of_address,
            first_name: values.firstName,
            last_name: values.lastName,
            company: values.company,
            address_details: addressPayload,
          };
        } else {
          if (!routeMembershipId) {
            alert("Select a user (Agent/Team Member) first.");
            return;
          }
          const requestBody = {
            label: values?.label_of_address,
            first_name: values?.firstName,
            last_name: values?.lastName,
            company: values?.company,
            address_details: addressPayload,
          };
          const data = await createMembershipProfileAddress(
            token,
            routeMembershipId,
            requestBody
          );
          addressId = String(data?.address_id || "").trim();
          if (!addressId) throw new Error("Failed to add address");
          createdRaw = {
            _id: addressId,
            label: values.label_of_address,
            first_name: values.firstName,
            last_name: values.lastName,
            company: values.company,
            address_details: addressPayload,
          };
        }

        const option = {
          value: addressId,
          label: String(createdRaw.label || values.label_of_address || "Address"),
          raw: createdRaw,
        };

        onCreated(createdRaw, option, useType);
        await Promise.resolve(afterCreateRefresh?.());
        onClose();
      } catch (err: any) {
        alert(err?.message || "Could not save address");
        // mark fields touched so errors show
        setTouched({
          label_of_address: true,
          firstName: true,
          lastName: true,
          phone: true,
          addressLine1: true,
          city: true,
          state: true,
          zipcode: true,
          country: true,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const hasGoogleCoordinates =
    Number(formik.values.latitude || 0) !== 0 &&
    Number(formik.values.longitude || 0) !== 0;

  const clearGoogleAddressFields = () => {
    formik.setFieldValue("city", "");
    formik.setFieldValue("cityCode", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("stateCode", "");
    formik.setFieldValue("zipcode", "");
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode);
    formik.setFieldValue("latitude", 0);
    formik.setFieldValue("longitude", 0);
  };

  const clearAddressDetailErrors = () => {
    (["city", "state", "zipcode", "country"] as const).forEach((field) => {
      formik.setFieldTouched(field, false, false);
      formik.setFieldError(field, undefined);
    });
  };

  const ensureManualCountryDefaults = () => {
    if (hasGoogleCoordinates || formik.values.country?.trim()) return;
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country, false);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode, false);
    formik.setFieldError("country", undefined);
  };

  const handleAddressLine1Change = (value: string) => {
    formik.setFieldValue("addressLine1", value);
    if (hasGoogleCoordinates || !value.trim()) {
      clearGoogleAddressFields();
      clearAddressDetailErrors();
    } else {
      ensureManualCountryDefaults();
    }
  };

  const handleSaveClick = async () => {
    if (!hasGoogleCoordinates && !formik.values.country?.trim()) {
      await formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country);
      await formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode);
      formik.setFieldError("country", undefined);
    }
    formik.handleSubmit();
  };

  if (!isOpen) return null;
  return (
    <>
      {/* :contentReference[oaicite:0]{index=0} */}
      <div
        className="modal show"
        tabIndex={-1}
        style={{ display: "block", background: "rgba(0,0,0,.5)", position: "fixed", inset: 0, zIndex: 1060 }}
        onClick={onClose}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" style={{ pointerEvents: "none" }}>
          <div
            className="modal-content rounded-4 overflow-hidden d-flex flex-column"
            id="newAddressAddmodal"
            style={{ 
              pointerEvents: "auto", 
              left: "15px !important",
              maxHeight: "90vh",
              height: "auto"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title */}
            <div className="px-4 pt-4 pb-3 border-bottom bg-white flex-shrink-0">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="m-0 d-flex align-items-center gap-2">
                  <i className="bi bi-house-door text-primary" />
                  <span>Add Address</span>
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
              </div>
            </div>

            {/* Section header */}
            <div className="px-4 pt-3 pb-1 flex-shrink-0">
              <div className="d-flex align-items-center gap-2 fw-semibold text-dark border-bottom pb-2">
                <i className="bi bi-geo-alt text-danger" />
                <span>Address Details</span>
              </div>
            </div>

            {/* Body */}
            <div className="modal-body pt-3 flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", maxHeight: "calc(90vh - 200px)", minHeight: 0 }}>
              <form onSubmit={formik.handleSubmit} noValidate>
                <div className="row g-3">
                  {/* Label of Address */}
                  <div className="col-12">
                    <label className="form-label">
                      Label of Address
                       {/* <span className="text-danger">*</span> */}
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="label_of_address"
                      name="label_of_address"
                      placeholder="Enter address label"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.label_of_address}
                    />
                    {/* {formik.touched.label_of_address && formik.errors.label_of_address && (
                      <div className="text-danger small mt-1">{formik.errors.label_of_address}</div>
                    )} */}
                  </div>

                  {/* First / Last */}
                  <div className="col-md-6">
                    <label className="form-label">
                      First Name
                       {/* <span className="text-danger">*</span> */}
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="firstName"
                      name="firstName"
                      placeholder="Enter first name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.firstName}
                    />
                    {/* {formik.touched.firstName && formik.errors.firstName && (
                      <div className="text-danger small mt-1">{formik.errors.firstName}</div>
                    )} */}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Last Name
                       {/* <span className="text-danger">*</span> */}
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="lastName"
                      name="lastName"
                      placeholder="Enter last name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.lastName}
                    />
                    {/* {formik.touched.lastName && formik.errors.lastName && (
                      <div className="text-danger small mt-1">{formik.errors.lastName}</div>
                    )} */}
                  </div>

                  {/* Phone / Company */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="phone"
                      name="phone"
                      placeholder="Enter phone number"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.phone}
                    />
                    {formik.touched.phone && formik.errors.phone && (
                      <div className="text-danger small mt-1">{formik.errors.phone}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="company"
                      name="company"
                      placeholder="Enter company name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.company}
                    />
                    {formik.touched.company && formik.errors.company && (
                      <div className="text-danger small mt-1">{formik.errors.company}</div>
                    )}
                  </div>

                  {/* Address 1 / 2 */}
                  <div className="col-12">
                    <label className="form-label">
                      Address Line 1 <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <i className="bi bi-geo-alt position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                      {isLoaded ? (
                        // ✅ new block
                        <div className="position-relative">
                          <AddressAutocomplete
                            id="addressLine1"
                            name="addressLine1"
                            value={formik.values.addressLine1}
                            onChange={handleAddressLine1Change}
                            // Omit 'countries' for global results. Add countries={['in']} ONLY if you want to restrict.
                            onSelect={(picked) => {
                              formik.setValues({
                                ...formik.values,
                                addressLine1: picked.addressLine1,
                                city: picked.city,
                                cityCode: picked.cityCode,
                                state: picked.state,
                                stateCode: picked.stateCode,
                                zipcode: picked.zipcode,
                                country: picked.country,
                                countryCode: picked.countryCode,
                                latitude: String(picked.latitude || ""),
                                longitude: String(picked.longitude || ""),
                              }, true);
                              clearAddressDetailErrors();
                            }}
                            className={`form-control ${formik.touched.addressLine1 && formik.errors.addressLine1 ? "is-invalid" : ""}`}
                            placeholder="Enter address line 1"
                            autoComplete="new-password"
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          className="form-control addr-field ps-5"
                          id="addressLine1"
                          name="addressLine1"
                          placeholder="Enter address line 1"
                          onChange={(e) => handleAddressLine1Change(e.target.value)}
                          onBlur={formik.handleBlur}
                          value={formik.values.addressLine1}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck={false}
                          required
                        />
                      )}
                    </div>
                    {formik.touched.addressLine1 && formik.errors.addressLine1 && (
                      <div className="text-danger small mt-1">{formik.errors.addressLine1}</div>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Address Line 2</label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="addressLine2"
                      name="addressLine2"
                      placeholder="Enter address line 2 (optional)"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.addressLine2}
                    />
                    {formik.touched.addressLine2 && formik.errors.addressLine2 && (
                      <div className="text-danger small mt-1">{formik.errors.addressLine2}</div>
                    )}
                  </div>

                  {/* City / State */}
                  <div className="col-md-6">
                    <label className="form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="city"
                      name="city"
                      placeholder="Enter city"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.city}
                      disabled
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    {formik.touched.city && formik.errors.city && (
                      <div className="text-danger small mt-1">{formik.errors.city}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="state"
                      name="state"
                      placeholder="State"
                      onBlur={formik.handleBlur}
                      value={formik.values.state}
                      disabled
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    {formik.touched.state && formik.errors.state && (
                      <div className="text-danger small mt-1">{formik.errors.state}</div>
                    )}
                  </div>

                  {/* Zip / Country */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Zip Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="zipcode"
                      name="zipcode"
                      placeholder="Enter zip code"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.zipcode}
                      disabled
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    {formik.touched.zipcode && formik.errors.zipcode && (
                      <div className="text-danger small mt-1">{formik.errors.zipcode}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Country <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control addr-field"
                      id="country"
                      name="country"
                      placeholder="United States"
                      onBlur={formik.handleBlur}
                      value={formik.values.country}
                      disabled
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    {formik.touched.country && formik.errors.country && (
                      <div className="text-danger small mt-1">{formik.errors.country}</div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 px-4 py-3 border-top bg-light-subtle flex-shrink-0">
              <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                onClick={handleSaveClick}
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? "Saving..." : "Add Address"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show" onClick={onClose} style={{ cursor: "pointer" }} />

      {/* lightweight cosmetics to match the reference UI */}
      <style>{`
      .addr-field{ border-radius:12px; background:#fff; }
      #newAddressAddmodal .addr-field:disabled {
        background-color: #f1f3f5 !important;
        color: #212529 !important;
        border-color: #cfd4da !important;
        opacity: 1;
        cursor: not-allowed;
      }
      .modal-content{ border:0; box-shadow: 0 10px 30px rgba(0,0,0,.12); }
      #newAddressAddmodal .modal-body{ 
        overflow-y: auto !important; 
        overflow-x: hidden !important;
        -webkit-overflow-scrolling: touch;
      }
      @media (min-width: 577px){
        #newAddressAddmodal .modal-body{ 
          max-height: calc(90vh - 200px) !important;
        }
      }
      @media (max-width:576px){
        .modal-dialog{ margin: .75rem; max-width: calc(100% - 1.5rem); }
        .modal-content{ max-height: 95vh !important; }
        #newAddressAddmodal .modal-body{ 
          max-height: calc(95vh - 200px) !important; 
          overflow-y: auto !important; 
          -webkit-overflow-scrolling: touch; 
        }
      }
      .bi-geo-alt { pointer-events: none; }
    `}</style>
    </>
  );

  // return (
  //     <>
  //         <div
  //             className="modal show"
  //             tabIndex={-1}
  //             style={{
  //                 display: "block",
  //                 background: "rgba(0,0,0,0.4)",
  //                 position: "fixed",
  //                 inset: 0,
  //                 zIndex: 1060,
  //             }}
  //             onClick={onClose}
  //         >
  //             <div
  //                 className="modal-dialog modal-lg modal-dialog-centered"
  //                 style={{ pointerEvents: "none" }}
  //             >
  //                 <div
  //                     className="modal-content"
  //                     id="newAddressAddmodal"
  //                     style={{ pointerEvents: "auto" }}
  //                     onClick={(e) => e.stopPropagation()}
  //                 >
  //                     <div className="modal-header">
  //                         <h5 className="modal-title ms-2">Add New Address</h5>
  //                         <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
  //                     </div>

  //                     <div className="modal-body">
  //                         <form onSubmit={formik.handleSubmit}>
  //                             <div className="row">
  //                                 {/* Label */}
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">
  //                                         Label of address<span className="text-danger"> *</span>
  //                                     </label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="label_of_address"
  //                                         name="label_of_address"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.label_of_address}
  //                                     />
  //                                     {formik.touched.label_of_address && formik.errors.label_of_address && (
  //                                         <div className="text-danger">{formik.errors.label_of_address}</div>
  //                                     )}
  //                                 </div>

  //                                 {/* First / Last */}
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">First Name<span className="text-danger ps-1">*</span></label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="firstName"
  //                                         name="firstName"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.firstName}
  //                                     />
  //                                     {formik.touched.firstName && formik.errors.firstName && (
  //                                         <div className="text-danger">{formik.errors.firstName}</div>
  //                                     )}
  //                                 </div>
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">Last Name<span className="text-danger ps-1">*</span></label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="lastName"
  //                                         name="lastName"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.lastName}
  //                                     />
  //                                     {formik.touched.lastName && formik.errors.lastName && (
  //                                         <div className="text-danger">{formik.errors.lastName}</div>
  //                                     )}
  //                                 </div>

  //                                 {/* Company / Phone */}
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">Company</label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="company"
  //                                         name="company"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.company}
  //                                     />
  //                                     {formik.touched.company && formik.errors.company && (
  //                                         <div className="text-danger">{formik.errors.company}</div>
  //                                     )}
  //                                 </div>
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">Phone number<span className="text-danger ps-1">*</span></label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="phone"
  //                                         name="phone"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.phone}
  //                                     />
  //                                     {formik.touched.phone && formik.errors.phone && (
  //                                         <div className="text-danger">{formik.errors.phone}</div>
  //                                     )}
  //                                 </div>

  //                                 {/* Address lines */}
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">
  //                                         Address Line 1
  //                                         <span className="text-danger ps-1">*</span>
  //                                     </label>
  //                                     {
  //                                         isLoaded &&
  //                                         <Autocomplete
  //                                             onLoad={(autocomplete) =>
  //                                                 (autocompleteRef.current = autocomplete)
  //                                             }
  //                                             onPlaceChanged={handlePlaceChanged}
  //                                         >
  //                                             <input
  //                                                 type="text"
  //                                                 className="form-control"
  //                                                 id="addressLine1"
  //                                                 name="addressLine1"
  //                                                 onChange={formik.handleChange}
  //                                                 onBlur={formik.handleBlur}
  //                                                 value={formik.values.addressLine1}
  //                                                 required
  //                                             />
  //                                         </Autocomplete>
  //                                     }
  //                                     {formik.touched.addressLine1 && formik.errors.addressLine1 ? (
  //                                         <div className="text-danger">
  //                                             {formik.errors.addressLine1}
  //                                         </div>
  //                                     ) : null}
  //                                 </div>
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">Address Line 2</label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="addressLine2"
  //                                         name="addressLine2"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.addressLine2}
  //                                     />
  //                                     {formik.touched.addressLine2 && formik.errors.addressLine2 && (
  //                                         <div className="text-danger">{formik.errors.addressLine2}</div>
  //                                     )}
  //                                 </div>

  //                                 {/* City / State / Zip / Country */}
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">City<span className="text-danger ps-1">*</span></label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="city"
  //                                         name="city"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.city}
  //                                     />
  //                                     {formik.touched.city && formik.errors.city && (
  //                                         <div className="text-danger">{formik.errors.city}</div>
  //                                     )}
  //                                 </div>
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">State<span className="text-danger ps-1">*</span></label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="state"
  //                                         name="state"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.state}
  //                                     />
  //                                     {formik.touched.state && formik.errors.state && (
  //                                         <div className="text-danger">{formik.errors.state}</div>
  //                                     )}
  //                                 </div>
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">Zip Code<span className="text-danger ps-1">*</span></label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="zipcode"
  //                                         name="zipcode"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.zipcode}
  //                                     />
  //                                     {formik.touched.zipcode && formik.errors.zipcode && (
  //                                         <div className="text-danger">{formik.errors.zipcode}</div>
  //                                     )}
  //                                 </div>
  //                                 <div className="col-md-6 mb-3">
  //                                     <label className="form-label">Country<span className="text-danger ps-1">*</span></label>
  //                                     <input
  //                                         type="text"
  //                                         className="form-control"
  //                                         id="country"
  //                                         name="country"
  //                                         onChange={formik.handleChange}
  //                                         onBlur={formik.handleBlur}
  //                                         value={formik.values.country}
  //                                     />
  //                                     {formik.touched.country && formik.errors.country && (
  //                                         <div className="text-danger">{formik.errors.country}</div>
  //                                     )}
  //                                 </div>
  //                             </div>
  //                         </form>
  //                     </div>

  //                     <div className="modal-footer">
  //                         <button type="button" className="btn btn-info" onClick={() => formik.handleSubmit()}>
  //                             {formik.isSubmitting ? "Saving..." : "Save Address"}
  //                         </button>
  //                         <button type="button" className="btn btn-danger" onClick={onClose}>
  //                             Cancel
  //                         </button>
  //                     </div>
  //                 </div>
  //             </div>
  //         </div>
  //         <div className="modal-backdrop fade show" onClick={onClose} style={{ cursor: "pointer" }} />
  //     </>
  // );
};

export default AddressFormModal;
